import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { farmerApi, volunteerApi, companyApi, mappingApi } from '../services/api';

// Create the context
const DataContext = createContext();

// Create a provider component
export const DataProvider = ({ children }) => {
  const [volunteers, setVolunteers] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [mappings, setMappings] = useState([]);

  // Define fetch functions outside of useEffect so they can be called directly
  const fetchVolunteers = async () => {
    try {
      console.log('Fetching volunteers...');
      const response = await volunteerApi.getAllVolunteers();
      console.log('Volunteers API response:', response);

      // Check if response is an array or has a results property
      const volunteersData = Array.isArray(response) ? response : response.results || [];

      // Map backend fields to frontend fields
      const mappedVolunteers = volunteersData.map(volunteer => ({
        id: volunteer.id,
        volunteerName: volunteer.name,
        mobile: volunteer.phone,
        village: volunteer.address,
        district: volunteer.location,
        username: volunteer.email,
        status: volunteer.status,
        createdAt: volunteer.created_at,
        updatedAt: volunteer.updated_at
      }));

      console.log('Mapped volunteers data:', mappedVolunteers);
      setVolunteers(mappedVolunteers);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
      setVolunteers([]); // Set empty array on error
    }
  };

  // Use a ref to track the last fetch time to prevent excessive fetching
  const lastCompanyFetchRef = useRef(0);

  const fetchCompanies = async (force = false) => {
    try {
      // Implement debouncing to prevent excessive API calls
      const now = Date.now();
      if (!force && now - lastCompanyFetchRef.current < 2000) {
        console.log('Skipping company fetch - too soon since last fetch');
        return companies; // Return current state if fetched recently
      }

      lastCompanyFetchRef.current = now;
      console.log('Fetching companies...');

      const response = await companyApi.getAllCompanies();
      console.log('Raw company response:', response);

      // Check if data is an array or has a results property (common in Django REST Framework)
      let companiesData = [];

      if (Array.isArray(response)) {
        companiesData = response;
        console.log('Response is an array, using directly');
      } else if (response && response.companies && Array.isArray(response.companies)) {
        // This is the new format from our updated backend
        companiesData = response.companies;
        console.log('Response has companies array, using response.companies');
      } else if (response && response.results && Array.isArray(response.results)) {
        companiesData = response.results;
        console.log('Response has results array, using response.results');
      } else if (response && typeof response === 'object') {
        // If it's an object but not in the expected format, try to extract data
        console.log('Unexpected response format, trying to extract data');

        // Try to find an array in the response
        for (const key in response) {
          if (Array.isArray(response[key])) {
            console.log(`Found array in response at key: ${key}`);
            companiesData = response[key];
            break;
          }
        }

        // If we still don't have data, try to convert the object to an array
        if (companiesData.length === 0 && Object.keys(response).length > 0) {
          console.log('Converting response object to array');
          companiesData = Object.values(response);

          // If the first item is an array, use that instead
          if (Array.isArray(companiesData[0])) {
            companiesData = companiesData[0];
          }
        }
      }

      console.log('Processed companies data:', companiesData);

      // Force update the state regardless of whether it has changed
      console.log('Updating companies state with', companiesData.length, 'companies');
      setCompanies(companiesData);

      return companiesData;
    } catch (error) {
      console.error('Error fetching companies:', error);
      console.error('Error details:', error.message);

      if (error.response) {
        console.error('Error response:', error.response.data);
      }

      // Only clear companies if we haven't loaded any yet
      if (companies.length === 0) {
        setCompanies([]);
      }

      throw error; // Re-throw to allow caller to handle
    }
  };

  // Load initial data
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    const fetchData = async () => {
      try {
        // Check if we have a valid token before making requests
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.log('No access token found, skipping data fetch');
          return;
        }

        // Initialize with empty arrays
        if (isMounted) {
          setVolunteers([]);
          setCompanies([]);
          setMappings([]);
        }

        console.log('Initializing data context with empty arrays');

        // Fetch data only if we have a valid token
        await Promise.all([
          fetchVolunteers().catch(error => {
            console.error('Error fetching volunteers:', error);
            return null;
          }),
          fetchCompanies().catch(error => {
            console.error('Error fetching companies:', error);
            return null;
          }),
          fetchFarmers().catch(error => {
            console.error('Error fetching farmers:', error);
            return null;
          })
        ]);

        retryCount = 0; // Reset retry count on success
      } catch (error) {
        console.error('Error in fetchData:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying fetch (${retryCount}/${maxRetries})...`);
          setTimeout(fetchData, 2000); // Wait 2 seconds before retrying
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array since we handle retries internally

  // Fetch farmers from API
  const fetchFarmers = async () => {
    try {
      const data = await farmerApi.getAllFarmers();
      // Check if data is an array or has a results property (common in Django REST Framework)
      const farmersData = Array.isArray(data) ? data : data.results || [];

      // Log the data to help with debugging
      console.log('Farmers data loaded:', farmersData);

      // Normalize the data to ensure consistent property names
      const normalizedFarmers = farmersData.map(farmer => ({
        ...farmer,
        // Ensure critical fields exist
        id: farmer.id,
        name: farmer.name || farmer.farmer_name || 'Unnamed Farmer',
        location: farmer.village || farmer.district || farmer.location || 'Unknown Location'
      }));

      setFarmers(normalizedFarmers);
    } catch (error) {
      console.error('Error fetching farmers:', error);
      setFarmers([]); // Set empty array on error
    }
  };

  // CRUD operations for volunteers using API
  const addVolunteer = async (volunteer) => {
    try {
      // Map frontend fields to backend fields
      const payload = {
        name: volunteer.name,
        email: volunteer.email,
        phone: volunteer.phone,
        address: volunteer.address,
        location: volunteer.location,
        skills: '',
        availability: '',
        status: volunteer.status || 'Active',
        password: volunteer.password
      };

      console.log('Creating volunteer with payload:', payload);
      const response = await volunteerApi.createVolunteer(payload);
      console.log('Created volunteer response:', response);

      // Map backend response to frontend fields
      const newVolunteer = {
        id: response.id,
        volunteerName: response.name,
        mobile: response.phone,
        village: response.address,
        district: response.location,
        username: response.email,
        status: response.status,
        createdAt: response.created_at,
        updatedAt: response.updated_at
      };

      console.log('Mapped volunteer data:', newVolunteer);

      // Update the volunteers list with the new volunteer
      setVolunteers(prevVolunteers => [...prevVolunteers, newVolunteer]);

      // Return the new volunteer with credentials if available
      return {
        ...newVolunteer,
        user_credentials: response.user_credentials
      };
    } catch (error) {
      console.error('Error adding volunteer:', error);
      throw error; // Re-throw the error to be handled by the component
    }
  };

  const updateVolunteer = async (id, updatedVolunteer) => {
    try {
      // Map frontend fields to backend fields
      const payload = {
        name: updatedVolunteer.name,
        email: updatedVolunteer.email,
        phone: updatedVolunteer.phone,
        address: updatedVolunteer.address,
        location: updatedVolunteer.location,
        status: updatedVolunteer.status,
        password: updatedVolunteer.password
      };

      console.log('Updating volunteer with payload:', payload);
      const result = await volunteerApi.updateVolunteer(id, payload);
      console.log('Updated volunteer:', result);

      // Map backend response to frontend fields
      const mappedResult = {
        id: result.id,
        volunteerName: result.name,
        mobile: result.phone,
        village: result.address,
        district: result.location,
        username: result.email,
        status: result.status,
        createdAt: result.created_at,
        updatedAt: result.updated_at
      };

      // Update the volunteers list with the mapped result
      setVolunteers(prevVolunteers =>
        prevVolunteers.map(volunteer => volunteer.id === id ? mappedResult : volunteer)
      );

      return mappedResult;
    } catch (error) {
      console.error('Error updating volunteer:', error);
      throw error;
    }
  };

  const deleteVolunteer = async (id) => {
    try {
      console.log('Deleting volunteer with ID:', id);
      await volunteerApi.deleteVolunteer(id);
      
      // Remove the volunteer from the state
      setVolunteers(prevVolunteers => prevVolunteers.filter(volunteer => volunteer.id !== id));
      
      console.log('Volunteer deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting volunteer:', error);
      throw error;
    }
  };

  // CRUD operations for farmers using API
  const addFarmer = async (farmer) => {
    try {
      const newFarmer = await farmerApi.createFarmer(farmer);
      setFarmers(prevFarmers => [...prevFarmers, newFarmer]);
      return newFarmer;
    } catch (error) {
      console.error('Error adding farmer:', error);
      throw error;
    }
  };

  const updateFarmer = async (id, updatedFarmer) => {
    try {
      const result = await farmerApi.updateFarmer(id, updatedFarmer);
      setFarmers(prevFarmers =>
        prevFarmers.map(farmer => farmer.id === id ? result : farmer)
      );
      return result;
    } catch (error) {
      console.error('Error updating farmer:', error);
      throw error;
    }
  };

  const deleteFarmer = async (id) => {
    try {
      await farmerApi.deleteFarmer(id);
      setFarmers(prevFarmers => prevFarmers.filter(farmer => farmer.id !== id));
      // Also delete any mappings associated with this farmer
      setMappings(prevMappings => prevMappings.filter(mapping => mapping.farmerId !== id));
    } catch (error) {
      console.error('Error deleting farmer:', error);
      throw error;
    }
  };

  // CRUD operations for companies using API
  const addCompany = async (company) => {
    try {
      console.log('Creating company with data:', company);

      // Make the API call with detailed error logging
      let response;
      try {
        response = await companyApi.createCompany(company);
        console.log('API Response for company creation:', response);
      } catch (apiError) {
        console.error('API Error Details:', apiError);
        console.error('API Error Response:', apiError.response);
        throw apiError;
      }

      // Add the new company to the state
      setCompanies(prevCompanies => [...prevCompanies, response]);

      // Fetch all companies to ensure the list is up to date
      await fetchCompanies();

      return response;
    } catch (error) {
      console.error('Error adding company:', error);

      // Show detailed error message to the user
      if (error.response && error.response.data) {
        console.error('Error response data:', error.response.data);

        // Handle different error formats
        if (error.response.data.detail) {
          alert(`Error: ${error.response.data.detail}`);
        } else if (error.response.data.errors) {
          const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
            .join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else if (typeof error.response.data === 'object') {
          // Try to extract error messages from the response data object
          const errorMessages = Object.entries(error.response.data)
            .map(([field, value]) => `${field}: ${value}`)
            .join('\n');
          alert(`Error:\n${errorMessages}`);
        } else {
          alert(`Error: ${JSON.stringify(error.response.data)}`);
        }
      } else if (error.message) {
        alert(`Error: ${error.message}`);
      } else {
        alert('Failed to create company. Please try again.');
      }

      throw error; // Re-throw the error to be handled by the caller
    }
  };

  const updateCompany = async (id, updatedCompany) => {
    try {
      const result = await companyApi.updateCompany(id, updatedCompany);
      setCompanies(prevCompanies =>
        prevCompanies.map(company => company.id === id ? result : company)
      );
      return result;
    } catch (error) {
      console.error('Error updating company:', error);
      // Fallback to local storage if API fails
      setCompanies(companies.map(company =>
        company.id === id ? {
          ...company,
          ...updatedCompany,
          // Ensure the company has all required fields for display
          name: updatedCompany.name || company.name || '',
          email: updatedCompany.email || company.email || '',
          phone: updatedCompany.phone || updatedCompany.mobile || company.phone || company.mobile || '',
          industry: updatedCompany.industry || company.industry || '',
          location: updatedCompany.location || company.location || '',
          address: updatedCompany.address || company.address || ''
        } : company
      ));
    }
  };

  const deleteCompany = async (id) => {
    try {
      // Call the API to delete the company (this will also delete the associated user)
      await companyApi.deleteCompany(id);

      // Update the local state to remove the company
      setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== id));

      // Also delete any mappings associated with this company
      setMappings(prevMappings => prevMappings.filter(mapping => mapping.companyId !== id));

      console.log(`Company with ID ${id} and its associated user account have been deleted`);
      return true;
    } catch (error) {
      console.error('Error deleting company:', error);

      // Show an error message to the user
      alert('Failed to delete the company. Please try again.');
      return false;
    }
  };

  // Operations for mappings
  const addMapping = async (companyId, farmerId) => {
    try {
      console.log('DataContext - Adding mapping:', { companyId, farmerId });
      
      // Check if mapping already exists in local state
      const existingMapping = mappings.find(m => {
        const mCompanyId = typeof m.companyId === 'string' ? parseInt(m.companyId) : m.companyId;
        const mFarmerId = typeof m.farmerId === 'string' ? parseInt(m.farmerId) : m.farmerId;
        const inputCompanyId = typeof companyId === 'string' ? parseInt(companyId) : companyId;
        const inputFarmerId = typeof farmerId === 'string' ? parseInt(farmerId) : farmerId;
        
        return mCompanyId === inputCompanyId && mFarmerId === inputFarmerId;
      });

      if (existingMapping) {
        console.log('DataContext - Mapping already exists in local state:', existingMapping);
        return { success: false, error: 'This farmer is already mapped to the selected company' };
      }

      // Create new mapping
      const newMapping = {
        id: Date.now(), // Temporary ID until server response
        companyId: parseInt(companyId),
        farmerId: parseInt(farmerId),
        status: 'active',
        mappedDate: new Date().toISOString()
      };

      console.log('DataContext - Creating new mapping:', newMapping);

      // Make API call to create mapping first
      const response = await mappingApi.createMapping({
        company_id: companyId,
        farmer_id: farmerId,
        status: 'active'
      });

      console.log('DataContext - API response:', response);

      // Only update local state if API call was successful
      if (response && response.created && response.created.length > 0) {
        const serverMapping = response.created[0];
        setMappings(prevMappings => [...prevMappings, {
          ...newMapping,
          id: serverMapping.id
        }]);
        return { success: true, mapping: serverMapping };
      }

      return { success: false, error: 'Failed to create mapping' };
    } catch (error) {
      console.error('DataContext - Error adding mapping:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to create mapping'
      };
    }
  };

  const deleteMapping = async (id) => {
    try {
      console.log('Deleting mapping with ID:', id);
      await mappingApi.deleteMapping(id);
      setMappings(prevMappings => prevMappings.filter(mapping => mapping.id !== id));
    } catch (error) {
      console.error('Error deleting mapping:', error);
      throw error;
    }
  };

  const updateMapping = async (id, updatedMapping) => {
    try {
      console.log('Updating mapping:', id, updatedMapping);
      const response = await mappingApi.updateMapping(id, updatedMapping);
      setMappings(prevMappings =>
        prevMappings.map(mapping => mapping.id === id ? response : mapping)
      );
      return response;
    } catch (error) {
      console.error('Error updating mapping:', error);
      throw error;
    }
  };

  // Helper function to get mapped farmers for a company
  const getMappedFarmersForCompany = (companyId) => {
    console.log('DataContext - getMappedFarmersForCompany called with companyId:', companyId);
    console.log('DataContext - All mappings:', mappings);

    const companyMappings = mappings.filter(m => m.companyId === companyId);
    console.log('DataContext - Filtered company mappings:', companyMappings);

    console.log('DataContext - All farmers:', farmers);

    const result = companyMappings.map(mapping => {
      console.log('DataContext - Processing mapping:', mapping);

      // Try multiple ways to find the farmer
      let farmer = null;

      // Method 1: Direct ID match
      farmer = farmers.find(f => f.id === mapping.farmerId);

      // Method 2: String comparison of IDs
      if (!farmer) {
        farmer = farmers.find(f => String(f.id) === String(mapping.farmerId));
      }

      // Method 3: Try to find by numeric part of ID
      if (!farmer && typeof mapping.farmerId === 'string' && mapping.farmerId.match(/\d+/)) {
        const numericId = mapping.farmerId.match(/\d+/)[0];
        farmer = farmers.find(f => String(f.id).includes(numericId));
      }

      // Method 4: If we have few farmers, just use the first one (temporary solution)
      if (!farmer && farmers.length > 0) {
        console.warn(`DataContext - No farmer found with ID ${mapping.farmerId}, using first available farmer as fallback`);
        farmer = farmers[0];
      }

      console.log('DataContext - Found farmer for mapping:', farmer);

      if (!farmer) {
        console.warn(`DataContext - No farmer found with ID ${mapping.farmerId}`);

        // If no farmer is found, return a placeholder
        return {
          id: mapping.farmerId,
          farmer_name: 'Unknown Farmer',
          name: 'Unknown Farmer',
          village: 'Unknown Location',
          mappingId: mapping.id,
          mappedDate: mapping.mappedDate,
          mappingStatus: mapping.status
        };
      }

      const mappedFarmer = {
        ...farmer,
        mappingId: mapping.id,
        mappedDate: mapping.mappedDate,
        mappingStatus: mapping.status
      };

      console.log('DataContext - Mapped farmer result:', mappedFarmer);
      return mappedFarmer;
    });

    console.log('DataContext - Final mapped farmers result:', result);
    return result;
  };

  return (
    <DataContext.Provider
      value={{
        volunteers,
        farmers,
        companies,
        mappings,
        addVolunteer,
        updateVolunteer,
        deleteVolunteer,
        addFarmer,
        updateFarmer,
        deleteFarmer,
        addCompany,
        updateCompany,
        deleteCompany,
        addMapping,
        deleteMapping,
        updateMapping,
        getMappedFarmersForCompany,
        fetchCompanies,
        fetchVolunteers,
        setMappings
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

// Custom hook to use the data context
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export default DataContext;

